import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { theme } from '../../domain/theme';
import { imageResponsiveService } from '../../domain/services/imageResponsiveService';
import { imageResizerService } from '../../domain/services/imageResizerService';
import { isArray, isObject } from '../../domain/utils/typeUtils';
import { ImageSources } from './components/ImageSources';
import { imageEntityPropShape } from './shapes/imageEntityPropShape';
import { ImageSchema } from './components/ImageSchema';
import { ImageSimple } from './ImageSimple';

const styles = {
  img: css`
    & img {
      display: block;
      // Keep width and height css, so images expand as required
      width: 100%;
      height: auto;
    }
  `
};
// "maxWidths" is an object to override the default breakpoint widths a image uses.
// It must be an object defining the "width" in px, and using the breakpoint name
// as a key: { mobile: 100, tablet: 200 }
// You don't need to set values for all breakpoints, only for the ones you want to override.
// If an image is not used in a breakpoint, set its value to null
export const Image = props => {
  const {
    entity: nonCheckedEntity,
    addSchema,
    aspect,
    quality,
    maxWidths,
    loading,
    preload,
    noStyle,
    noImgStyle,
    noFallbackWidthLimit,
    noAlt,
    noTitle,
    onLoad
  } = props;

  // Check if entity is valid.
  let entity = nonCheckedEntity;
  const isValidEntity = imageResizerService.isValidEntity(entity);
  if (!isValidEntity) {
    entity = { mediaImage: { url: '/images/transparent.png' } };
  }

  // Define the maxWidth, ratio and quality of each breakpoint.
  const displayConfig = {};
  theme.breakpoints.sorted.forEach(bp => {
    // maxWidths can contain a "null" value for a breakpoint that should be hidden.
    // maxWidth can only be null when a "null" value is set for a breakpoint.
    let maxWidth;
    if (maxWidths && Object.prototype.hasOwnProperty.call(maxWidths, bp.name)) {
      maxWidth = maxWidths[bp.name];
    } else {
      // When no maxWidth is defined, we use the max width of that
      // breakpoint minus 1 pixel, except for large_desktop
      maxWidth = bp.name === 'large_desktop' ? bp.width : bp.width - 1;
    }

    // Aspect can be an string or an object with breakpoint keys. When it is an object,
    // it can contain a "null" value for a breakpoint that should be hidden.
    // selectedAspect can only be null when a "null" value is set for a breakpoint. Otherwise,
    // it would be undefined.
    const selectedAspect =
      aspect && Object.prototype.hasOwnProperty.call(aspect, bp.name)
        ? aspect?.[bp.name]
        : aspect;
    let aspectRatio = null;
    let hidden = false;
    // If maxWidth or selectedAspect is null, set this breakpoint as hidden
    if (maxWidth === null || selectedAspect === null) {
      hidden = true;
    } else {
      aspectRatio = selectedAspect
        ? imageResizerService.getAspectRatioFromString(selectedAspect)
        : imageResizerService.getAspectRatioFromEntity(entity);
    }
    const displayQuality =
      quality ?? imageResizerService.getImageEntityQuality(entity.quality);

    // At this moment, all breakpoints must use the same quality.
    displayConfig[bp.name] = {
      maxWidth,
      aspectRatio,
      quality: displayQuality,
      hidden
    };
  });

  // Alter displayConfig before is used to crete sources.
  if (noFallbackWidthLimit) {
    displayConfig.large_desktop.maxWidth = 3200;
  }

  // Get sources by breakpoint
  const sourcesByBreakpoint = imageResponsiveService.getSources(
    entity,
    displayConfig
  );

  const fallbackImage = imageResponsiveService.getFallbackImage(
    entity,
    displayConfig
  );

  // If aspect is an object, there are different aspects and ratios
  // for each breakpoint. The same happens when some breakpoint is hidden.
  // Otherwise, use the first element from displayConfig to get its ratio,
  // since all of them are the same.
  if (!noStyle) {
    let stylesPicture;
    if (
      isObject(aspect) ||
      Object.values(displayConfig).includes(conf => conf.hidden)
    ) {
      stylesPicture = Object.keys(displayConfig).map(bpName => {
        const { aspectRatio, hidden } = displayConfig[bpName];

        // Hide image if this breakpoint is hidden
        if (hidden) {
          return theme.mq[bpName]
            ? `${theme.mq[bpName]} { display: none; }`
            : 'display: none';
        }

        // Reset display to its initial value just in case this breakpoint is after a hidden one
        return theme.mq[bpName]
          ? `${theme.mq[bpName]} { display: block; aspect-ratio: ${aspectRatio.width} / ${aspectRatio.height}; }`
          : `display: block; aspect-ratio: ${aspectRatio.width} / ${aspectRatio.height};`;
      });
    } else {
      const { aspectRatio } = Object.values(displayConfig)[0];
      stylesPicture = `aspect-ratio: ${aspectRatio.width} / ${aspectRatio.height};`;
    }

    styles.picture = css`
      ${isArray(stylesPicture) ? stylesPicture.join(' ') : stylesPicture}
    `;
  }

  return (
    <picture
      className="resp-img-cntr"
      css={[styles.picture, noImgStyle ? null : styles.img]}
    >
      <ImageSources sources={sourcesByBreakpoint} preload={preload} />
      <ImageSimple
        src={fallbackImage.url}
        alt={noAlt ? null : fallbackImage.alt}
        title={noTitle ? null : fallbackImage.title}
        width={fallbackImage.width}
        height={fallbackImage.height}
        loading={loading}
        onLoad={onLoad}
      />
      {addSchema && isValidEntity && <ImageSchema entity={entity} />}
    </picture>
  );
};

Image.defaultProps = {
  entity: undefined,
  aspect: undefined,
  quality: undefined,
  addSchema: false,
  maxWidths: {},
  loading: 'lazy',
  preload: false,
  noStyle: false,
  noImgStyle: false,
  noFallbackWidthLimit: false,
  noAlt: false,
  noTitle: false,
  onLoad: undefined
};

Image.propTypes = {
  entity: imageEntityPropShape,
  addSchema: PropTypes.bool,
  aspect: PropTypes.oneOfType([
    PropTypes.string, // '1x2', '2x3', '4x3', '4x5', '3x4', '2x1', '3x1', '3x2', '3x4', '5x4', '16x9', '1x1',
    // passing an array of aspect is only valid when loading is lazy
    PropTypes.objectOf(PropTypes.string)
  ]),
  quality: PropTypes.number,
  maxWidths: PropTypes.shape({
    mobile: PropTypes.number,
    tablet: PropTypes.number,
    small_desktop: PropTypes.number,
    large_desktop: PropTypes.number
  }),
  loading: PropTypes.oneOf(['eager', 'lazy']),
  preload: PropTypes.bool,
  noStyle: PropTypes.bool,
  noImgStyle: PropTypes.bool,
  noFallbackWidthLimit: PropTypes.bool,
  noAlt: PropTypes.bool,
  noTitle: PropTypes.bool,
  onLoad: PropTypes.func
};
